class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.checkoutButton = this.querySelector('.cart__checkout-button');
    this.setHeaderCartIconAccessibility();

    this.checkoutButton?.addEventListener('click', this.cartFormSubmit.bind(this));
  }

  cartFormSubmit(e) {
    e.target.classList.add('loading');
    e.target.setAttribute('aria-disabled', 'true');
    e.target.querySelector('.loading__spinner')?.classList.remove('hidden');

    setTimeout(() => {
      e.target.classList.remove('loading');
      e.target.setAttribute('aria-disabled', 'false');
      e.target.querySelector('.loading__spinner')?.classList.add('hidden');
    }, 3000);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.type != 'drawer') return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    document.querySelector('cart-order-summary')?.stickyBlock();
    if (this.dataset.type != 'drawer') {
      return;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, shouldOpen = true) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (shouldOpen) {
      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.open();
      });
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartOrderSummary extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.enhancePricing();

    document.addEventListener('DOMContentLoaded', () => {
      this.enhancePricing();
      this.stickyBlock();
    });

    requestAnimationFrame(() => {
      this.enhancePricing();
      this.stickyBlock();
    });

    setTimeout(() => {
      this.enhancePricing();
      this.stickyBlock();
    }, 300);
  }

  enhancePricing() {
  const cartDrawer = this.closest('#CartDrawer') || document.getElementById('CartDrawer');
  if (!cartDrawer) return;

  const pricingRow =
    cartDrawer.querySelector('[data-jb-cart-pricing-source]') ||
    cartDrawer.querySelector('[data-jb-cart-pricing]');

  if (pricingRow && pricingRow.parentElement !== this) {
    pricingRow.hidden = false;
    pricingRow.removeAttribute('data-jb-cart-pricing-source');
    pricingRow.setAttribute('data-jb-cart-pricing', '');
    this.prepend(pricingRow);
  }

  this.querySelectorAll('.shopify-block').forEach((block) => {
    const oldSubtotalFooter = block.querySelector('.cart-drawer__footer');
    if (!oldSubtotalFooter) return;

    const hasOldTotal =
      oldSubtotalFooter.querySelector('.totals') ||
      oldSubtotalFooter.querySelector('.dbtfy-cart-saving');

    if (!hasOldTotal) return;

    const hasDiscountCode = Array.from(
      oldSubtotalFooter.querySelectorAll('.discounts__discount')
    ).some((discount) => discount.textContent.trim().length > 0);

    block.classList.add('jb-cart-subtotal-block');

    if (hasDiscountCode) {
      block.classList.add('jb-cart-subtotal-block--has-discount');
      block.classList.remove('jb-cart-subtotal-block--empty');
      block.removeAttribute('aria-hidden');
    } else {
      block.classList.add('jb-cart-subtotal-block--empty');
      block.classList.remove('jb-cart-subtotal-block--has-discount');
      block.setAttribute('aria-hidden', 'true');
    }
  });

  this.querySelectorAll('.shopify-block').forEach((block) => {
    if (block.querySelector('.cart__ctas, .cart__checkout-button')) {
      block.classList.add('jb-cart-checkout-block');
    }

    if (block.querySelector('trust-badge, .dbtfy-trust-badge')) {
      block.classList.add('jb-cart-trust-block');
    }
  });
}

  stickyBlock() {
    const _this = this;

    setTimeout(function () {
      if (
        window.Debutify &&
        Debutify.widgets &&
        Debutify.widgets['cart-order-summary'] &&
        Debutify.widgets['cart-order-summary'].sticky_block
      ) {
        const blockHeight = _this.offsetHeight;
        _this.classList.add('sticky-block');
        _this.style.setProperty('--sticky-block-height', `${blockHeight}px`);
        document.querySelector('.cart-drawer')?.style.setProperty('--sticky-block-height', `${blockHeight}px`);

        if (blockHeight == 0 && !document.body.classList.contains('empty-cart')) {
          _this.stickyBlock();
        }
      }
    }, 500);
  }
}

customElements.define('cart-order-summary', CartOrderSummary);